// FileUploadContext.jsx
// Responsibility: Managing file attachments for the current prompt
import React, { createContext, useContext, useState, useCallback } from 'react';
import * as apiFiles from '../api/files';
import { useAuth } from './AuthContext';
import { useChatSession } from './ChatSessionContext';

const FileUploadContext = createContext();

export const FileUploadProvider = ({ children }) => {
    // --- File Upload States ---
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [webSearchEnabled, setWebSearchEnabled] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    // Get contexts
    const { userId, isSessionValid, setAuthError, setIsSessionValid } = useAuth();
    const { submitMessage, inputValue, setInputValue, isWaitingForResponse, isLimitReached } = useChatSession();

    // --- Add Files ---
    const addFiles = useCallback((files) => {
        console.log(`FileUploadContext: User ${userId} adding ${files.length} files`);
        
        setUploadError(null);

        const newFiles = files.map(file => ({
            id: crypto.randomUUID(),
            file: file,
            status: 'pending',
            progress: 0,
            uploadedUrl: null,
            processedContent: null,
            userId: userId // Track file owner
        }));
    
        setPendingFiles(prev => [...prev, ...newFiles]);
    }, [userId]);

    // --- Remove File ---
    const removeFile = useCallback((fileId) => {
        console.log(`FileUploadContext: Removing file ${fileId}`);
        setPendingFiles(prev => prev.filter(f => f.id !== fileId));
        
        // Also remove from uploading list if it's there
        setUploadingFiles(prev => prev.filter(id => id !== fileId));
    }, []);

    // --- Upload Files ---
    const uploadFiles = async (files) => {
        console.log(`FileUploadContext: User ${userId} uploading ${files.length} files`);

        // Check session before upload
        if (!isSessionValid) {
            const error = 'Session expired. Please sign in again.';
            setUploadError(error);
            throw new Error(error);
        }

        setUploadError(null);
        
        const uploadPromises = files.map(async (fileInfo) => {
            // Already uploaded in a previous attempt — reuse existing s3Key, skip re-upload
            if (fileInfo.status === 'uploaded' && fileInfo.s3Key) {
                return {
                    id: fileInfo.id,
                    name: fileInfo.file.name,
                    size: fileInfo.file.size,
                    type: fileInfo.file.type,
                    s3Key: fileInfo.s3Key,
                    userId: userId,
                };
            }

            try {
                setUploadingFiles(prev => [...prev, fileInfo.id]);
                
                // Update file status to uploading
                setPendingFiles(prev => prev.map(f => 
                    f.id === fileInfo.id 
                        ? { ...f, status: 'uploading' }
                        : f
                ));
                
                // Get presigned URL with authentication
                const presignedData = await apiFiles.getPresignedUrl(fileInfo.file.type);
                
                // Upload to S3 with progress tracking
                await apiFiles.uploadToS3(presignedData.uploadUrl, fileInfo.file, (progress) => {
                    setPendingFiles(prev => prev.map(f => 
                        f.id === fileInfo.id 
                            ? { ...f, progress, status: 'uploading' }
                            : f
                    ));
                });
                
                // Mark as processing
                setPendingFiles(prev => prev.map(f => 
                    f.id === fileInfo.id 
                        ? { ...f, status: 'processing', progress: 100 }
                        : f
                ));
                
                // Trigger backend processing with authentication
                await apiFiles.triggerFileProcessing(
                    presignedData.objectKey, 
                    fileInfo.file.name
                );
                
                // Mark as uploaded
                setPendingFiles(prev => prev.map(f => 
                    f.id === fileInfo.id 
                        ? { ...f, status: 'uploaded', s3Key: presignedData.objectKey }
                        : f
                ));
                
                console.log(`FileUploadContext: Successfully uploaded file ${fileInfo.file.name}`);
                
                return {
                    id: fileInfo.id,
                    name: fileInfo.file.name,
                    size: fileInfo.file.size,
                    type: fileInfo.file.type,
                    s3Key: presignedData.objectKey,
                    userId: userId
                };
                
            } catch (error) {
                console.error(`FileUploadContext: Upload failed for file ${fileInfo.file.name}:`, error);
                
                // Handle authentication errors specifically
                if (error.message?.includes('Authentication failed') || 
                    error.message?.includes('Session expired') ||
                    error.message?.includes('401')) {
                    setAuthError(error.message);
                    setIsSessionValid(false);
                    setUploadError('Authentication failed. Please sign in again.');
                } else {
                    setUploadError(`Failed to upload ${fileInfo.file.name}: ${error.message}`);
                }
                
                // Mark file as error
                setPendingFiles(prev => prev.map(f => 
                    f.id === fileInfo.id 
                        ? { ...f, status: 'error', error: error.message }
                        : f
                ));
                
                throw error;
                
            } finally {
                setUploadingFiles(prev => prev.filter(id => id !== fileInfo.id));
            }
        });
        
        try {
            const results = await Promise.all(uploadPromises);
            console.log(`FileUploadContext: User ${userId} upload complete:`, results);
            return results;
        } catch (error) {
            console.error('FileUploadContext: One or more uploads failed:', error);
            throw error;
        }
    };

    // --- Handle Submit with Files ---
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        // Check authentication
        if (!isSessionValid) {
            setAuthError('Your session has expired. Please sign in again.');
            return;
        }

        if (isLimitReached || (!inputValue.trim() && pendingFiles.length === 0) || isWaitingForResponse) {
            console.log('FileUploadContext: Submit blocked - validation failed');
            return;
        }

        const userPrompt = inputValue.trim();
        
        if (!userPrompt && pendingFiles.length === 0) {
            console.log('FileUploadContext: No input or files to submit');
            return;
        }

        let uploadedFiles = [];

        // Upload files if any are pending
        if (pendingFiles.length > 0) {
            console.log(`FileUploadContext: Uploading ${pendingFiles.length} pending files`);
            
            try {
                uploadedFiles = await uploadFiles(pendingFiles);
                console.log(`FileUploadContext: Successfully uploaded ${uploadedFiles.length} files`);
            } catch (error) {
                console.error(`FileUploadContext: File upload failed:`, error);
                
                // Don't submit if file upload failed
                return;
            }
        }

        // Clear input immediately (optimistic — message will appear in chat)
        setInputValue('');

        // Submit message with uploaded files
        try {
            await submitMessage(userPrompt, uploadedFiles, webSearchEnabled);
            // Only clear files after the message was successfully submitted.
            // If the LLM call fails, files are preserved so the user can retry
            // without re-attaching (already-uploaded files skip the S3 step).
            setPendingFiles([]);
            setUploadError(null);
            console.log('FileUploadContext: Message submitted successfully');
        } catch (error) {
            console.error('FileUploadContext: Failed to submit message:', error);
            // Error is displayed in chat by ChatSessionContext
        }
    };

    // --- Get Upload Status ---
    const getUploadStatus = useCallback(() => {
        const total = pendingFiles.length;
        const uploading = pendingFiles.filter(f => f.status === 'uploading').length;
        const processing = pendingFiles.filter(f => f.status === 'processing').length;
        const uploaded = pendingFiles.filter(f => f.status === 'uploaded').length;
        const errors = pendingFiles.filter(f => f.status === 'error').length;
        
        return {
            total,
            uploading,
            processing,
            uploaded,
            errors,
            isUploading: uploading > 0 || processing > 0,
            hasErrors: errors > 0,
            allUploaded: uploaded === total && total > 0
        };
    }, [pendingFiles]);

    // --- Reset File Upload State ---
    const resetFileUpload = useCallback(() => {
        console.log('FileUploadContext: Resetting file upload state');
        setPendingFiles([]);
        setUploadingFiles([]);
        setUploadError(null);
        setWebSearchEnabled(false);
    }, []);

    const value = {
        // State
        pendingFiles,
        uploadingFiles,
        webSearchEnabled,
        uploadError,

        // Actions
        addFiles,
        removeFile,
        setUploadError,
        setWebSearchEnabled,
        handleSubmit,
        getUploadStatus,
        resetFileUpload
    };

    return (
        <FileUploadContext.Provider value={value}>
            {children}
        </FileUploadContext.Provider>
    );
};

export const useFileUpload = () => {
    const context = useContext(FileUploadContext);
    if (context === undefined) {
        throw new Error('useFileUpload must be used within a FileUploadProvider');
    }
    return context;
};